import {
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import AppTable from "../../common/app-table";
import { useAuth } from "../../hooks/AuthProvider";
import {
  LIST_CREATE_CLIMATRONICS_CUSTOMERS,
  EDIT_CLIMATRONICS_CUSTOMERS,
  BULK_CREATE_CLIMATRONICS_CUSTOMERS,
  EXPORT_CLIMATRONICS_CUSTOMERS,
} from "../../config/urls";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import { CallingCodeSelect } from "../../common/country-code-select";
import {
  resolveSearchQueryForApi,
  normalizeCombinedSearchInput,
} from "../../utils/phoneSearch";
import { formatWithTzLabel } from "../../utils/dateUtils";

// ─── Constants ───────────────────────────────────────────────────────────────

const COLUMNS = [
  { label: "Name", id: "name" },
  { label: "Country Code", id: "wa_country_code" },
  { label: "WhatsApp Phone Number", id: "wa_phone_no" },
  { label: "Other Phone Number", id: "other_phone_no" },
  { label: "Email", id: "email" },
  { label: "Interested Product", id: "interested_product" },
  { label: "Organization", id: "organization" },
  { label: "Last Updated", id: "updated_at" },
];

const EMPTY_FORM = {
  name: "",
  wa_phone_no: "",
  wa_country_code: "",
  other_phone_no: "",
  email: "",
  organization: "",
};

// Country calling code: 1-4 digits, no leading zero, optional leading "+"
// (covers all real ITU/E.164 calling codes, e.g. "1", "91", "971", "1264").
const COUNTRY_CODE_REGEX = /^\+?[1-9]\d{0,3}$/;

// WhatsApp phone number: digits only, 6-15 digits (E.164 national-number range).
const PHONE_NUMBER_REGEX = /^\d{6,15}$/;

const CSV_TEMPLATE =
  "name,wa_country_code,wa_phone_no,other_phone_no,email,registered,interested_product,organization\nJohn Doe,91,1234567890,0987654321,john@example.com,yes,Product A,Org A\nJane Doe,91,1234567891,0987654322,jane@example.com,No,Product B,Org B\n";

// ─── CSV Import Dialog ────────────────────────────────────────────────────────
const CsvImportDialog = ({
  open,
  onClose,
  onImported,
  getValidToken,
  tenant,
}) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [uploadError, setUploadError] = useState(null); // { summary, details[] }
  const [showAllErrors, setShowAllErrors] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null);
      setUploading(false);
      setResult(null);
      setUploadError(null);
      setShowAllErrors(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [open]);

  const handleFileChange = (e) => {
    const chosen = e.target.files?.[0];
    if (!chosen) return;
    if (!chosen.name.endsWith(".csv")) {
      setUploadError({
        summary: "Only .csv files are supported.",
        details: [],
      });
      return;
    }
    setFile(chosen);
    setResult(null);
    setUploadError(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);

    try {
      const token = await getValidToken();
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(BULK_CREATE_CLIMATRONICS_CUSTOMERS, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-ID": tenant,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        if (data?.errors?.length > 0) {
          // Validation errors — show per-row details
          setUploadError({
            summary: `${data.errors.length} row${data.errors.length !== 1 ? "s" : ""} have validation errors. No records were saved.`,
            details: data.errors.map(
              (e) => `Row ${e.row} — ${e.field}: ${e.message}`,
            ),
          });
        } else {
          setUploadError({
            summary: data?.detail || "Upload failed. Please try again.",
            details: [],
          });
        }
        return;
      }

      setResult(data);
      onImported();
    } catch (err) {
      console.error("CSV upload error:", err);
      setUploadError({
        summary: "Upload failed. Please check your connection and try again.",
        details: [],
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customers_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Import Leads via CSV
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
          p={2}
          width="100%"
        >
          {/* Template download link */}
          <Typography variant="body2" component="span">
            Click{" "}
            <Typography
              variant="body2"
              component="span"
              onClick={downloadTemplate}
              sx={{
                textDecoration: "none",
                cursor: "pointer",
                color: "blue",
              }}
            >
              here
            </Typography>{" "}
            to download the sample template
          </Typography>

          <Typography variant="body2" sx={{ color: "grey" }}>
            Required columns: name, wa_country_code, wa_phone_no, registered
          </Typography>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            hidden
            onChange={handleFileChange}
          />
          <Button
            variant="contained"
            component="span"
            startIcon={<UploadFileIcon />}
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            Select File
          </Button>

          {/* Selected file name */}
          {file && (
            <Box textAlign="center">
              <Typography variant="subtitle1">Selected File:</Typography>
              <Typography variant="body2">{file.name}</Typography>
            </Box>
          )}

          {/* Upload progress */}
          {uploading && <LinearProgress sx={{ width: "100%" }} />}

          {/* Error alert */}
          {uploadError && (
            <Alert severity="error" sx={{ width: "100%" }}>
              <Typography variant="body1" fontWeight="bold">
                {uploadError.summary}
              </Typography>
              {uploadError.details.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {(showAllErrors
                    ? uploadError.details
                    : uploadError.details.slice(0, 5)
                  ).map((errMsg, index) => (
                    <li key={index}>{errMsg}</li>
                  ))}
                </ul>
              )}
              {uploadError.details.length > 5 && (
                <Typography
                  variant="body2"
                  sx={{
                    cursor: "pointer",
                    color: "blue",
                    textDecoration: "underline",
                    mt: 1,
                  }}
                  onClick={() => setShowAllErrors(!showAllErrors)}
                >
                  {showAllErrors ? "Show less..." : "Read more..."}
                </Typography>
              )}
            </Alert>
          )}

          {/* Success alert */}
          {result && (
            <Alert severity="success" sx={{ width: "100%" }}>
              <Typography variant="body1" fontWeight="bold">
                {result.created > 0 &&
                  `${result.created} lead${result.created !== 1 ? "s" : ""} created. `}
                {result.updated > 0 &&
                  `${result.updated} lead${result.updated !== 1 ? "s" : ""} updated.`}
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, p: 2 }}>
        <ButtonTwo
          title="Cancel"
          variant="outlined"
          onClick={onClose}
          disabled={uploading}
        />
        {!result && (
          <ButtonOne
            title={uploading ? "Uploading…" : "Upload & Import"}
            variant="contained"
            onClick={handleUpload}
            disabled={uploading || !file}
            startIcon={
              uploading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <UploadFileIcon />
              )
            }
          />
        )}
      </Box>
    </Dialog>
  );
};

// ─── Single Customer Form Dialog ──────────────────────────────────────────────

const CustomerFormDialog = ({
  open,
  onClose,
  customer,
  onSaved,
  getValidToken,
  tenant,
}) => {
  const isEdit = Boolean(customer?.id);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(customer ? { ...EMPTY_FORM, ...customer } : EMPTY_FORM);
      setErrors({});
    }
  }, [open, customer]);

  const validate = () => {
    const errs = {};

    if (!form.name?.trim()) errs.name = "Name is required";

    const countryCode = (form.wa_country_code ?? "").trim();
    if (!countryCode) {
      errs.wa_country_code = "Country Code is required";
    } else if (!COUNTRY_CODE_REGEX.test(countryCode)) {
      errs.wa_country_code =
        "Enter a valid country code (digits only, e.g. 91)";
    }

    const phoneNo = (form.wa_phone_no ?? "").trim();
    if (!phoneNo) {
      errs.wa_phone_no = "WhatsApp Phone Number is required";
    } else if (!PHONE_NUMBER_REGEX.test(phoneNo)) {
      errs.wa_phone_no =
        "Enter a valid phone number (digits only, 6-15 digits)";
    }
    const otherPhoneNo = (form.other_phone_no ?? "").trim();
    if (otherPhoneNo && !PHONE_NUMBER_REGEX.test(otherPhoneNo)) {
      errs.other_phone_no =
      "Enter a valid phone number (digits only, 6-15 digits)";
    }

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = "Enter a valid email address";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    let error = "";
    switch (name) {
      case "wa_country_code":
        if (value && !COUNTRY_CODE_REGEX.test(value)) {
          error = "Enter a valid country code";
        }
        break;
        case "wa_phone_no":
        case "other_phone_no":
          if (value && !PHONE_NUMBER_REGEX.test(value)) {
            error = "Enter a valid phone number";
          }
          break;
          case "email":
            if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
              error = "Enter a valid email address";
            }
            break;
            default:
              break;
            }
            setErrors((prev) => ({
              ...prev,
              [name]: error || undefined,
            }));
          };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const token = await getValidToken();
      const url = isEdit
        ? EDIT_CLIMATRONICS_CUSTOMERS(customer.id)
        : LIST_CREATE_CLIMATRONICS_CUSTOMERS;

      const response = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Tenant-ID": tenant,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      if (!response.ok) throw new Error("Failed to save lead");
      const saved = await response.json();
      onSaved(saved, isEdit);
      onClose();
    } catch (err) {
      console.error("Error saving lead:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {isEdit ? "Edit Lead" : "New Lead"}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2.5}>
          <TextField
            label="Name *"
            name="name"
            value={form.name}
            onChange={handleChange}
            error={Boolean(errors.name)}
            helperText={errors.name}
            fullWidth
            size="small"
          />
          <CallingCodeSelect
            label="WhatsApp Country Code *"
            value={form.wa_country_code}
            onChange={(code) => handleChange({ target: { name: "wa_country_code", value: code } })}
            size="small"
            TextFieldProps={{ error: Boolean(errors.wa_country_code), helperText: errors.wa_country_code }}
          />
          <TextField
            label="WhatsApp Phone Number *"
            name="wa_phone_no"
            value={form.wa_phone_no}
            onChange={handleChange}
            error={Boolean(errors.wa_phone_no)}
            helperText={errors.wa_phone_no}
            fullWidth
            size="small"
          />
          <TextField
            label="Other Phone Number"
            name="other_phone_no"
            value={form.other_phone_no}
            onChange={handleChange}
            error={Boolean(errors.other_phone_no)}
            helperText={errors.other_phone_no}
            fullWidth
            size="small"
          />
          <TextField
            label="Email"
            name="email"
            value={form.email}
            onChange={handleChange}
            error={Boolean(errors.email)}
            helperText={errors.email}
            fullWidth
            size="small"
            type="email"
          />
          <TextField
            label="Registered"
            name="registered"
            value={form.registered}
            onChange={handleChange}
            fullWidth
            size="small"
            select
          >
            <MenuItem value={true}>Yes</MenuItem>
            <MenuItem value={false}>No</MenuItem>
          </TextField>

          <TextField
            label="Organization"
            name="organization"
            value={form.organization}
            onChange={handleChange}
            fullWidth
            error={Boolean(errors.organization)}
            size="small"
          />
          <TextField
            label="Interested Product"
            name="interested_product"
            value={form.interested_product}
            onChange={handleChange}
            fullWidth
            size="small"
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              pt: 0.5,
            }}
          >
            <ButtonTwo
              title="Cancel"
              variant="outlined"
              onClick={onClose}
              disabled={saving}
              sx={{ minWidth: 90 }}
            />
            <ButtonOne
              title={saving ? "Saving…" : isEdit ? "Update" : "Create"}
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
              sx={{ minWidth: 90 }}
              startIcon={
                saving ? <CircularProgress size={14} color="inherit" /> : null
              }
            />
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Customers Page ──────────────────────────────────────────────────────

const Leads = () => {
  const { getValidToken } = useAuth();
  const tenant = localStorage.getItem("tenant");

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const [searchText, setSearchText] = useState("");
  const searchTimerRef = useRef(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);

  const fetchCustomers = useCallback(
    async (search = searchText, currentPage = page, perPage = rowsPerPage) => {
      const token = await getValidToken();
      setLoading(true);
      try {
        const params = new URLSearchParams({
          registered: false,
          offset: currentPage * perPage,
          limit: perPage,
        });
        const trimmed = (search ?? "").trim();
        const r = resolveSearchQueryForApi(trimmed);
        if (r.blockFetch) {
          setLoading(false);
          return;
        }
        if (r.apiSearchQuery) params.set("search", r.apiSearchQuery);

        const response = await fetch(
          `${LIST_CREATE_CLIMATRONICS_CUSTOMERS}?${params}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Tenant-ID": tenant,
            },
          },
        );
        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();
        setCustomers(data.results);
        setTotalCount(data.count || 0);
      } catch (err) {
        console.error("Error fetching leads:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [getValidToken, tenant, searchText, page, rowsPerPage],
  );

  useEffect(() => {
    fetchCustomers(searchText, page, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handleSearchChange = (e) => {
    const value = normalizeCombinedSearchInput(e.target.value);
    setSearchText(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(0);
      fetchCustomers(value, 0, rowsPerPage);
    }, 400);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openCreate = () => {
    setSelectedCustomer(null);
    setDialogOpen(true);
  };

  const openEdit = (customer) => {
    setSelectedCustomer(customer);
    setDialogOpen(true);
  };

  const handleSaved = (saved, isEdit) => {
    if (isEdit) {
      setCustomers((prev) => prev.map((c) => (c.id === saved.id ? saved : c)));
    } else {
      fetchCustomers(searchText, 0, rowsPerPage);
      setPage(0);
    }
  };

  const handleCsvImported = () => {
    fetchCustomers(searchText, 0, rowsPerPage);
    setPage(0);
  };

  const handleExport = async () => {
    let url = EXPORT_CLIMATRONICS_CUSTOMERS;

    // Construct query parameters for server-side filtering and pagination
    let queryParams = new URLSearchParams({
      registered: false,
    });
    if (searchText.trim()) queryParams.set("search", searchText.trim());

    url += `?${queryParams}`;

    const token = await getValidToken();

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
      });

      if (!response.ok) {
        try {
          const error = (await response.json()).error;
          alert(error);
          throw new Error(error);
        } catch {
          throw new Error("Failed to export data");
        }
      }

      // Handle CSV response - clone response for blob processing
      const responseClone = response.clone();
      const blob = await responseClone.blob();

      // Create a link element to download the file
      const urlBlob = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = urlBlob;

      // Use the current date for the filename
      const today = new Date();
      const dateStr = today.toISOString().split("T")[0];
      a.download = `climatronics-leads-${dateStr}.csv`;

      // Append to the document and trigger download
      document.body.appendChild(a);
      a.click();

      // Clean up
      window.URL.revokeObjectURL(urlBlob);
      document.body.removeChild(a);
      return; // Exit after successful CSV download
    } catch (error) {
      console.error("Error exporting data:", error.message);
      throw error;
    }
  };

  return (
    <Stack sx={{ p: 4 }}>
      {/* Toolbar */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <TextField
          placeholder="Search by name, phone, email…"
          value={searchText}
          onChange={handleSearchChange}
          size="small"
          sx={{ width: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1.5}>
          <ButtonTwo
            title="Export CSV"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
          <ButtonTwo
            title="Import CSV"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => setCsvDialogOpen(true)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
          <ButtonOne
            title="New Lead"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
        </Stack>
      </Stack>

      <AppTable
        columns={[
          ...COLUMNS.map((col) => ({
            id: col.id,
            label: col.label,
            minWidth: col.id === "name" ? undefined : "160px",
            render:
              col.id === "name"
                ? (row) => (
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#1D2330", fontSize: "14px" }}
                    >
                      {row.name}
                    </Typography>
                  )
                : col.id === "interested_product"
                ? (row) => (
                    <Tooltip title={row[col.id] || ""} arrow>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: "220px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row[col.id] || "N/A"}
                      </Typography>
                    </Tooltip>
                  )
                : col.id === "updated_at"
                ? (row) =>
                    row.updated_at_iso
                      ? formatWithTzLabel(row.updated_at_iso)
                      : "N/A"
                : (row) => row[col.id] || "N/A",
          })),
          {
            id: "__actions__",
            label: "Actions",
            minWidth: "80px",
            render: (row) => (
              <IconButton
                size="small"
                onClick={() => openEdit(row)}
                sx={{ color: "primary.main" }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            ),
          },
        ]}
        rows={customers}
        loading={loading}
        emptyMessage={searchText ? `No leads found for "${searchText}"` : "No Leads Found"}
        count={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        paperSx={{ border: "1px solid #DCDFE5", borderRadius: "12px", mb: 2 }}
        containerSx={{
          maxHeight: "calc(100vh - 260px)",
          scrollbarWidth: "thin",
          scrollbarColor: "#C5CEDB #F3F6FB",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": { backgroundColor: "#F3F6FB", borderRadius: "8px" },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#C5CEDB", borderRadius: "8px" },
          "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#A8B4C7" },
        }}
        rowSx={(row, index) => ({
          backgroundColor: index % 2 === 0 ? "#F0F5FF" : "#FFFFFF",
          cursor: "default",
          "&:hover": { backgroundColor: index % 2 === 0 ? "#F5F5F5" : "#F7F9FC" },
          "& td": { borderBottom: "none", borderTop: "none" },
        })}
      />

      <CustomerFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        customer={selectedCustomer}
        onSaved={handleSaved}
        getValidToken={getValidToken}
        tenant={tenant}
      />

      <CsvImportDialog
        open={csvDialogOpen}
        onClose={() => setCsvDialogOpen(false)}
        onImported={handleCsvImported}
        getValidToken={getValidToken}
        tenant={tenant}
      />
    </Stack>
  );
};

export default Leads;